import type jsPDF from 'jspdf';
import type { UnreturnedMissedCall } from '@/types';
import { BrandPdf, PDF_COLORS } from '@/lib/pdf/brandPdf';
import type { PdfAssets } from '@/lib/pdf/pdfAssets';
import { formatDay, formatGenerated } from './callCostReport';
import { csvText } from './extensionReports';

/** The Unreturned Missed report's labels, summary, CSV and PDF, for the Reports page and scheduled reports. */

export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

export function missTypeLabel(type: string): string {
  const t = type.toLowerCase();
  if (t === 'no_answer' || t === 'noanswer') return 'No Answer';
  if (t === 'busy') return 'Busy';
  if (t === 'abandoned') return 'Abandoned';
  return type || '—';
}

export function destinationLabel(type: string): string {
  const t = type.toLowerCase();
  if (t === 'extension') return 'Extension';
  if (t === 'queue') return 'Queue';
  if (t === 'ringgroup' || t === 'ring_group') return 'Ring Group';
  return type || '—';
}

export function isReturned(row: UnreturnedMissedCall): boolean {
  return Number(row.unreturn_status) === 1;
}

/** Who hung up — shown under CDR status as “Hung up by …”. */
export function hungUpByLabel(disconnectedBy?: string): string {
  const by = String(disconnectedBy || '').toLowerCase();
  if (by === 'external') return 'Hung up by: external caller (gate / visitor / outside line)';
  if (by === 'extension') return 'Hung up by: extension (internal party)';
  if (by === 'system') return 'Hung up by: PBX system (timeout / forced release)';
  if (disconnectedBy) return `Hung up by: ${disconnectedBy}`;
  return '';
}

export function summarizeMissedCalls(rows: UnreturnedMissedCall[]) {
  const byDest: Record<string, number> = {};
  const byMiss: Record<string, number> = {};
  rows.forEach((r) => {
    const dest = destinationLabel(r.call_to_type);
    const miss = missTypeLabel(r.miss_call_type);
    byDest[dest] = (byDest[dest] || 0) + 1;
    byMiss[miss] = (byMiss[miss] || 0) + 1;
  });
  const cdrIncludes = (r: UnreturnedMissedCall, text: string) => String(r.cdrDisposition || '').toLowerCase().includes(text);
  return {
    total: rows.length,
    unreturned: rows.filter((r) => !isReturned(r)).length,
    returned: rows.filter((r) => isReturned(r)).length,
    zeroRing: rows.filter((r) => (r.ring_duration || 0) <= 0).length,
    cdrBusy: rows.filter((r) => cdrIncludes(r, 'busy')).length,
    cdrAbandoned: rows.filter((r) => cdrIncludes(r, 'abandon')).length,
    mismatch: rows.filter((r) => r.dispositionMismatch).length,
    byDest,
    byMiss,
  };
}

export function missedCallsCsv(rows: UnreturnedMissedCall[]) {
  return csvText(
    [
      'Time',
      'Call From',
      'Call To',
      'Destination Type',
      'Missed Type (report)',
      'CDR Status',
      'Hung up by',
      'Disposition mismatch',
      'Ext ring (s)',
      'Call length (s)',
      'Callback Status',
      'Last Callback Time',
      'Trunk',
      'DID',
      'Hangup reason',
      'Ext registered (now)',
      'Ext devices (now)',
      'Ext presence (now)',
      'What happened',
      'ID',
    ],
    rows.map((r) => [
      r.time,
      r.call_from,
      r.call_to,
      destinationLabel(r.call_to_type),
      missTypeLabel(r.miss_call_type),
      r.cdrDisposition || '',
      hungUpByLabel(r.cdrDisconnectedBy) || r.cdrDisconnectedBy || '',
      r.dispositionMismatch ? 'Yes' : 'No',
      r.ring_duration,
      r.cdrDurationSeconds ?? '',
      isReturned(r) ? 'Returned' : 'Unreturned',
      r.return_time || '',
      r.srcTrunk || '',
      r.didNumber || '',
      r.cdrReason || '',
      r.extRegistered == null ? '' : r.extRegistered ? 'Yes' : 'No',
      r.extDevices || '',
      r.extPresence || '',
      r.what_happened || '',
      r.id,
    ])
  );
}

const c = PDF_COLORS;

export function missedCallsPdf(input: {
  rows: UnreturnedMissedCall[];
  filtersText: string[];
  startDate: string;
  endDate: string;
  customerName?: string;
  assets?: PdfAssets;
  now?: Date;
}): jsPDF {
  const { rows } = input;
  const summary = summarizeMissedCalls(rows);
  const doc = new BrandPdf({
    orientation: 'landscape',
    assets: input.assets,
    reportName: 'Unreturned missed calls',
    title: 'Missed calls',
    subtitle: `${formatDay(input.startDate)} – ${formatDay(input.endDate)}  ·  ${input.filtersText.join('  ·  ')}`,
    customerName: input.customerName,
    generated: `Generated ${formatGenerated(input.now ?? new Date())}`,
  });
  doc.statTiles([
    { label: 'Missed calls', value: String(summary.total), caption: 'Matching the filters', featured: true },
    { label: 'Not called back', value: String(summary.unreturned), dot: c.red, caption: 'No callback recorded' },
    { label: 'Called back', value: String(summary.returned), dot: c.green, caption: 'Returned later' },
    { label: 'Never rang', value: String(summary.zeroRing), dot: c.orange, caption: '0s ring on the phone' },
  ]);
  doc.table({
    title: 'Missed calls',
    meta: `${rows.length} call${rows.length === 1 ? '' : 's'}`,
    columns: [
      { header: 'Time', width: 30 },
      { header: 'Caller', width: 34 },
      { header: 'Called', width: 34 },
      { header: 'Type', width: 22 },
      { header: 'CDR status', width: 24 },
      { header: 'Ring', width: 12, align: 'right' },
      { header: 'Callback', width: 22 },
      { header: 'Last callback', width: 28 },
      { header: 'What happened', width: 74, wrap: true },
    ],
    rows: rows.map((row) => [
      row.time,
      row.call_from || '—',
      row.call_to ? `${row.call_to} · ${destinationLabel(row.call_to_type)}` : '—',
      missTypeLabel(row.miss_call_type),
      row.cdrDisposition || '—',
      formatDuration(row.ring_duration || 0),
      isReturned(row)
        ? { text: 'Returned', color: c.green, badge: true }
        : { text: 'Unreturned', color: c.red, badge: true },
      row.return_time || '—',
      row.what_happened || '',
    ]),
  });
  return doc.finish();
}
